console.log('z-carousel.js loaded');

// Global collection of slide state objects for z-carousel
// Each item has the shape:
// { element, sWidth: number, offCenter: number, active: boolean, visible: boolean, fully: boolean }
let slides = [];

// Current translate3d X offset applied to the container (in px)
// and the index of the logically "active" slide
let currentOffset = 0;
let currentSlide = 0;

// Drag/swipe state
let isDragging = false;
let dragStartX = 0;
let dragStartOffset = 0;
let lastDragOffset = 0;

/**
 * Applies a horizontal translate3d offset (in px) to the first
 * `.z-carousel__container` found on the page.
 *
 * @param {number} ofst - The horizontal offset in pixels. Positive values
 *   move the slides to the right, negative values to the left.
 */
function setZCarouselOffset(ofst) {
  let container = document.querySelector('.z-carousel__container');
  if (!container) return;

  let offsetNumber = Number(ofst) || 0;
  container.style.transform = 'translate3d(' + offsetNumber + 'px, 0px, 0px)';

   // Keep logical state in sync with the visual offset
   updateCurrentSlideState(offsetNumber);
}

// Expose globally for testing and future use
window.setZCarouselOffset = setZCarouselOffset;

/**
 * Updates currentOffset/currentSlide and the active flag on slides
 * based on the provided translate3d offset.
 */
function updateCurrentSlideState(offsetNumber) {
  currentOffset = offsetNumber;

  if (!slides.length) {
    currentSlide = 0;
    return;
  }

  let closestIndex = 0;
  let smallestDiff = Infinity;

  for (let i = 0; i < slides.length; i += 1) {
    let slide = slides[i];
    // Reset active flag; we'll set it for the closest slide below
    slide.active = false;

    // Use offCenter if available to find which slide this offset best matches
    let expectedOffset = typeof slide.offCenter === 'number' ? slide.offCenter : 0;
    let diff = Math.abs(expectedOffset - offsetNumber);
    if (diff < smallestDiff) {
      smallestDiff = diff;
      closestIndex = i;
    }
  }

  currentSlide = closestIndex;
  slides[currentSlide].active = true;

  // Expose for debugging / inspection
  window.zCarouselCurrentOffset = currentOffset;
  window.zCarouselCurrentSlide = currentSlide;

  // Reflect current slide in the pagination UI
  updateZCarouselPagination();
}

/**
 * Initializes the slides array from current DOM.
 * Captures each slide's width in pixels (sWidth).
 */
function initZCarouselSlides() {
  let slideNodes = document.querySelectorAll('.z-carousel__slide');
  slides = Array.prototype.map.call(slideNodes, function (el) {
    let rect = el.getBoundingClientRect();

    return {
      element: el,
      sWidth: rect.width,
      offCenter: 0,
      active: false,
      visible: false,
      fully: false
    };
  });

  // After widths are known, compute the offCenter offset for each slide
  updateZCarouselOffsets();

  // On initial load, center the first slide if we have any slides
  if (slides.length > 0) {
    setZCarouselOffset(slides[0].offCenter);
  }

  // Make it easy to inspect from DevTools
  window.zCarouselSlides = slides;
}

/**
 * Syncs the "1/4" indicator with currentSlide and slides.length.
 */
function updateZCarouselPagination() {
  let pagination = document.querySelector('.z-carousel__pagination');
  if (!pagination) return;

  let currentEl = pagination.querySelector('.z-carousel__current');
  let totalEl = pagination.querySelector('.z-carousel__total');

  if (currentEl) {
    currentEl.textContent = String(currentSlide + 1);
  }

  if (totalEl) {
    totalEl.textContent = String(slides.length);
  }
}

/**
 * Computes offCenter (the translate3d X value to center each slide)
 * using the recurrence you described.
 */
function updateZCarouselOffsets() {
  if (!slides.length) return;

  let container = document.querySelector('.z-carousel__container');
  if (!container) return;

  let styles = window.getComputedStyle(container);
  let gapValue = styles.gap || styles.columnGap || '0';
  let gap = parseFloat(gapValue) || 0;
  let viewportWidth = window.innerWidth;

  // Track the natural left position (without any transform) so we can
  // center all slides except the last one, which should stop flush-right.
  let leftPosition = 0;

  for (let i = 0; i < slides.length; i += 1) {
    let slide = slides[i];

    if (i === slides.length - 1) {
      // Last slide: align its right edge with the viewport's right edge.
      // leftPosition + slide.sWidth + offset = viewportWidth
      slide.offCenter = viewportWidth - (leftPosition + slide.sWidth);
    } else {
      // Other slides: center in the viewport as before.
      slide.offCenter = (viewportWidth / 2) - (leftPosition + (slide.sWidth / 2));
    }

    leftPosition += slide.sWidth + gap;
  }

  // Keep global reference up to date for inspection
  window.zCarouselSlides = slides;
}

/**
 * Syncs the "1/4" indicator with currentSlide and slides.length.
 */
function updateZCarouselPagination() {
  let pagination = document.querySelector('.z-carousel__pagination');
  if (!pagination) return;

  let currentEl = pagination.querySelector('.z-carousel__current');
  let totalEl = pagination.querySelector('.z-carousel__total');

  if (currentEl) {
    currentEl.textContent = String(currentSlide + 1);
  }

  if (totalEl) {
    totalEl.textContent = String(slides.length);
  }
}

/**
 * Binds click handlers for the prev/next buttons in the footer controls.
 */
function initZCarouselControls() {
  let prevButton = document.querySelector('.z-carousel__button--prev');
  let nextButton = document.querySelector('.z-carousel__button--next');

  if (prevButton) {
    prevButton.addEventListener('click', zCarouselPrev);
  }

  if (nextButton) {
    nextButton.addEventListener('click', zCarouselNext);
  }
}

/**
 * Enables horizontal dragging/swiping of the carousel container and
 * snaps to the closest slide on release.
 */
function initZCarouselDragging() {
  let container = document.querySelector('.z-carousel__container');
  if (!container) return;

  // Prevent native drag behaviour on media so swipe works when dragging images
  container.addEventListener('dragstart', function (event) {
    event.preventDefault();
  });

  let mediaNodes = container.querySelectorAll('img, picture, video');
  mediaNodes.forEach(function (node) {
    node.setAttribute('draggable', 'false');
  });

  container.addEventListener('pointerdown', function (event) {
    // Only primary button / touch
    if (event.button !== 0 && event.pointerType === 'mouse') return;

    isDragging = true;
    dragStartX = event.clientX;
    dragStartOffset = currentOffset;
    lastDragOffset = currentOffset;

    // Disable transition during direct dragging
    container.style.transition = 'none';

    try {
      container.setPointerCapture(event.pointerId);
    } catch (e) {
      // Older browsers may not support setPointerCapture; safe to ignore
    }
  });

  container.addEventListener('pointermove', function (event) {
    if (!isDragging) return;

    let deltaX = event.clientX - dragStartX;
    let proposedOffset = dragStartOffset + deltaX;

    // Constrain offset between first and last slide centers
    if (slides.length) {
      let minOffset = Infinity;
      let maxOffset = -Infinity;
      for (let i = 0; i < slides.length; i += 1) {
        let off = slides[i].offCenter;
        if (off < minOffset) minOffset = off;
        if (off > maxOffset) maxOffset = off;
      }
      proposedOffset = Math.max(minOffset, Math.min(maxOffset, proposedOffset));
    }

    lastDragOffset = proposedOffset;
    container.style.transform = 'translate3d(' + proposedOffset + 'px, 0px, 0px)';
  });

  function endDrag(event) {
    if (!isDragging) return;
    isDragging = false;

    try {
      container.releasePointerCapture(event.pointerId);
    } catch (e) {
      // Ignore if capture was not set
    }

    // Re-enable CSS transition for snapping animation
    container.style.transition = '';

    // If we have slides, snap to the nearest one
    if (slides.length) {
      let snapFrom = lastDragOffset;
      // Determine which slide is closest to this offset
      updateCurrentSlideState(snapFrom);
      let targetOffset = slides[currentSlide].offCenter;
      setZCarouselOffset(targetOffset);
    } else {
      // No slides: just make sure state is consistent
      setZCarouselOffset(0);
    }
  }

  container.addEventListener('pointerup', endDrag);
  container.addEventListener('pointercancel', endDrag);
}
/**
 * Handles viewport resize:
 * - Re-measures slide widths
 * - Recomputes offCenter for every slide
 * - Recenters the previously active slide (if any)
 */
function handleZCarouselResize() {
  initZCarouselSlides();

  if (!slides.length) return;

  let targetIndex = currentSlide;
  if (targetIndex < 0 || targetIndex >= slides.length) {
    targetIndex = 0;
  }

  // Use the precomputed offCenter for that slide
  let targetOffset = slides[targetIndex].offCenter;
  setZCarouselOffset(targetOffset);
}

// Initialize slides and controls once DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function () {
    initZCarouselSlides();
    initZCarouselControls();
    initZCarouselDragging();
  });
} else {
  initZCarouselSlides();
  initZCarouselControls();
  initZCarouselDragging();
}

// Debounced resize listener to avoid excessive recalculation
let zCarouselResizeTimer;
window.addEventListener('resize', function () {
  clearTimeout(zCarouselResizeTimer);
  zCarouselResizeTimer = setTimeout(handleZCarouselResize, 200);
});

/**
 * Moves to the next slide, if any, using the precomputed offCenter.
 */
function zCarouselNext() {
  if (!slides.length) return;

  let targetIndex = currentSlide + 1;
  if (targetIndex >= slides.length) {
    targetIndex = slides.length - 1;
  }

  let targetOffset = slides[targetIndex].offCenter;
  setZCarouselOffset(targetOffset);
}

/**
 * Moves to the previous slide, if any, using the precomputed offCenter.
 */
function zCarouselPrev() {
  if (!slides.length) return;

  let targetIndex = currentSlide - 1;
  if (targetIndex < 0) {
    targetIndex = 0;
  }

  let targetOffset = slides[targetIndex].offCenter;
  setZCarouselOffset(targetOffset);
}

// Expose helpers globally for now
window.zCarouselNext = zCarouselNext;
window.zCarouselPrev = zCarouselPrev;
